import { mergeStyleSets, getTheme, ITheme, IDocumentCardStyles } from '@fluentui/react';

const theme: ITheme = getTheme();

export const crmPageStyles = mergeStyleSets({
  container: {
    padding: '12px 16px',
    minHeight: '100vh',
    background: theme.palette.neutralLighterAlt,
  },
  caseGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: '16px',
    selectors: {
      '@media (max-width: 768px)': {
        gridTemplateColumns: '1fr',
        gap: '12px',
      },
    },
  },
  caseCardHeader: {
    padding: '16px 16px 8px 16px',
    borderBottom: `1px solid ${theme.palette.neutralLighter}`,
  },
  caseCardBody: {
    padding: '12px 16px',
    flex: 1,
  },
  caseCardFooter: {
    padding: '10px 16px 14px 16px',
    borderTop: `1px solid ${theme.palette.neutralLighter}`,
    background: theme.palette.neutralLighterAlt,
  },
  label: {
    fontSize: theme.fonts.tiny.fontSize,
    textTransform: 'uppercase' as const,
    color: theme.palette.neutralSecondary, // Changed from neutralTertiary for WCAG AA contrast (4.5:1)
    fontWeight: 600,
    letterSpacing: '0.05em',
  },
  value: {
    fontSize: theme.fonts.small.fontSize,
    color: theme.palette.neutralPrimary,
    fontWeight: 500,
  },
  emptyState: {
    minHeight: 280,
    background: theme.palette.white,
    borderRadius: 12,
    border: `1px dashed ${theme.palette.neutralQuaternary}`,
    textAlign: 'center' as const,
    padding: 40,
    selectors: {
      '@media (max-width: 768px)': {
        padding: 20,
        minHeight: 200,
      },
    },
  },
  spinnerContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 200,
  },
  caseCount: {
    color: theme.palette.neutralSecondary,
    fontSize: theme.fonts.small.fontSize,
    lineHeight: '32px',
    minWidth: 72,
    textAlign: 'right' as const,
  },
  searchHint: {
    display: 'block',
    color: theme.palette.neutralSecondary,
    fontSize: theme.fonts.tiny.fontSize,
    lineHeight: '16px',
    marginTop: 2,
  },
  paginationBar: {
    marginTop: 16,
    padding: '10px 12px',
    background: theme.palette.white,
    border: `1px solid ${theme.palette.neutralLight}`,
    borderRadius: 8,
  },
  pageButton: {
    minWidth: 28,
    height: 28,
    lineHeight: '28px',
    border: 'none',
    borderRadius: 3,
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 400,
    backgroundColor: 'transparent',
    color: theme.palette.neutralPrimary,
    padding: '0 6px',
    textAlign: 'center' as const,
  },
  selectedPageButton: {
    minWidth: 28,
    height: 28,
    lineHeight: '28px',
    border: 'none',
    borderRadius: 3,
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 700,
    backgroundColor: theme.palette.themePrimary,
    color: theme.palette.white,
    padding: '0 6px',
    textAlign: 'center' as const,
  },
  paginationEllipsis: {
    minWidth: 20,
    height: 28,
    lineHeight: '28px',
    fontSize: 14,
    color: theme.palette.neutralSecondary,
    textAlign: 'center' as const,
    userSelect: 'none' as const,
  },
  paginationCount: {
    fontSize: 12,
    color: theme.palette.neutralSecondary,
  },
  employeeName: {
    fontWeight: 700,
    fontSize: theme.fonts.mediumPlus.fontSize,
    color: theme.palette.neutralPrimary,
  },
  caseSubtitle: {
    fontSize: theme.fonts.xSmall.fontSize,
    color: theme.palette.neutralSecondary,
    marginTop: 2,
  },
  duplicateBadge: {
    fontSize: theme.fonts.tiny.fontSize,
    background: theme.palette.yellowLight,
    color: theme.palette.yellow,
    padding: '2px 8px',
    borderRadius: 10,
    fontWeight: 600,
  },
  emptyIcon: {
    fontSize: 48,
  },
  emptyTitle: {
    fontSize: theme.fonts.large.fontSize,
    fontWeight: 600,
    color: theme.palette.neutralPrimary,
    marginTop: 12,
  },
  emptySubtitle: {
    color: theme.palette.neutralSecondary,
    marginTop: 4,
  },
});

export const caseCardStyles: IDocumentCardStyles = {
  root: {
    borderRadius: 10,
    border: `1px solid ${theme.palette.neutralLight}`,
    background: theme.palette.white,
    boxShadow: theme.effects.elevation4,
    overflow: 'hidden' as const,
    transition: 'box-shadow 0.15s ease',
    display: 'flex',
    flexDirection: 'column' as const,
  },
};

export const alertBannerStyles = { root: { marginBottom: 8, borderRadius: 6 } };

export const actionsBarStyles = {
  root: {
    marginBottom: 16,
    padding: '12px 36px',
    background: theme.palette.white,
    border: `1px solid ${theme.palette.neutralLight}`,
    borderRadius: 8,
    boxShadow: theme.effects.elevation4,
  },
};

export const compactButtonStyles = {
  root: { borderRadius: 6, fontSize: 12, height: 30 },
};

export const searchBoxStyles = {
  root: {
    width: 360,
    borderRadius: 4,
    borderColor: theme.palette.neutralLight,
    selectors: {
      '@media (max-width: 1025px)': { width: 280 },
      '@media (max-width: 768px)': { width: '100%' },
    },
  },
};
